const INSET_SHADOW = "inset 0.86px 1.721px 10px 0px rgba(0, 0, 0, 0.1)";
const INSET_SHADOW_LIGHT =
  "inset 0.86px 1.721px 10px 0px rgba(255, 255, 255, 0.25)";

export interface ProgressSegmentSpec {
  left: number;
  top?: number;
  width: number;
  height: number;
  fill: string;
  shadow?: string;
}

/** Stacked inset progress segment — Figma toolkit preview bars (375×14 track). */
export function ProgressSegment({
  left,
  top = 0,
  width,
  height,
  fill,
  shadow = INSET_SHADOW,
}: ProgressSegmentSpec) {
  return (
    <div
      aria-hidden
      className="absolute overflow-hidden rounded-[43px]"
      style={{ left, top, width, height }}
    >
      <div
        className="absolute inset-0 rounded-[43px]"
        style={{ backgroundColor: fill, boxShadow: shadow }}
      />
    </div>
  );
}

export interface ProgressBarLayer {
  width: number;
  fill: string;
  shadow?: string;
  /** Figma ml offset — full-width layers use 0, caps use 0.31. */
  insetLeft?: number;
  /** Figma mt offset — full-width layers use 0, caps use 0.1–0.12. */
  insetTop?: number;
  /** Layer height — full-width layers match track height, caps are ~0.15px shorter. */
  height?: number;
}

interface InsetProgressBarProps {
  /** Total track width in px (Figma: 375 desktop, scales with canvas). */
  trackWidth?: number;
  trackHeight?: number;
  /** Layers drawn back-to-front; last layer is the accent cap. */
  layers: ProgressBarLayer[];
  className?: string;
}

function defaultShadow(fill: string, explicit?: string) {
  if (explicit) return explicit;
  return fill === "#001EFF" ||
    fill === "#F76E6E" ||
    fill === "#FFCDB5" ||
    fill === "#ABF0B7" ||
    fill === "#ACC7FF"
    ? INSET_SHADOW_LIGHT
    : INSET_SHADOW;
}

/**
 * Layered inset progress track used in Fund tab card previews (Figma 11752/11767).
 * Layers stack in one grid cell — each is a width cap from the left edge.
 */
export function InsetProgressBar({
  trackWidth = 375,
  trackHeight = 14,
  layers,
  className,
}: InsetProgressBarProps) {
  return (
    <div
      aria-hidden
      className={className}
      style={{ width: trackWidth, height: trackHeight, position: "relative" }}
    >
      {layers.map((layer, index) => (
        <ProgressSegment
          key={index}
          left={layer.insetLeft ?? (index % 2 === 0 ? 0 : 0.31)}
          top={layer.insetTop ?? (index % 2 === 0 ? 0 : 0.12)}
          width={layer.width}
          height={
            layer.height ??
            (index % 2 === 0 ? trackHeight : trackHeight - 0.149)
          }
          fill={layer.fill}
          shadow={defaultShadow(layer.fill, layer.shadow)}
        />
      ))}
    </div>
  );
}
